// Palworld TCG community keyword cluster: finding players, running and
// attending events, safe community trading, judging community price talk and
// building a local playgroup.
//
// No topic here overlaps an existing guide: card trading value mechanics live
// in guides-tcg-features-2026.ts, sanctioned event structure lives in
// guides-organized-play.ts, and rules live in guides-tcg-rules.ts. This cluster
// owns the "where do I find people, and how do I deal with them" query family.
// Five AEO-shaped articles per topic (overview, step-by-step, reference,
// mistakes, FAQ) keeps every title unique and every answer extractable.

import type { Guide, GuideArticle } from "./guides-data";
import { OFFICIAL_LINKS } from "./official-links";

interface Topic {
  slug: string;
  title: string;
  desc: string;
  keywords: string;
  hub: string;
  hubLabel: string;
  tag: string;
  question: string;
  answer: string;
  steps: [string, string][];
  data: [string, string][];
  pitfalls: [string, string][];
  faq: [string, string][];
}

const TOPICS: Topic[] = [
  {
    slug: "palworld-tcg-community-finder",
    title: "Palworld TCG Community Finder",
    desc: "How to find real Palworld TCG players: which shops carry the game, how to read event listings, and how to vet an online group before you trade in it.",
    keywords:
      "palworld tcg community, find palworld tcg players, palworld card game near me, palworld tcg groups, palworld tcg players online",
    hub: "/trades",
    hubLabel: "Palworld TCG trade board",
    tag: "TCG Community",
    question: "How do you find other Palworld TCG players?",
    answer:
      "Start with card shops that already run other Bushiroad titles, because they are the most likely to stock and schedule Palworld play. Then add one moderated online group for pairing and trading, and use a card-code-based trade board so swaps can be valued before anything ships. Vetting matters more than volume: a small group with pinned rules and enforced trade formatting is worth more than a busy channel full of unsourced price claims.",
    steps: [
      ["Call shops that run Bushiroad titles", "Stores already running other Bushiroad games have the distribution and the judge coverage, so they schedule Palworld play first. Ask which night, whether it is casual or sanctioned, and how many players usually show."],
      ["Read the event listing properly", "A listing that names the format, entry fee and start time is a shop that runs events well. Vague listings usually mean an unscheduled casual night, which is still a fine place to start."],
      ["Add one moderated online group", "You only need one. Pick the group with pinned rules citing the publisher and a trade channel that requires printed card codes."],
      ["Move trades onto a code-based board", "Once you know who you are dealing with, post the exact printed codes you have and need so both sides can be valued against live data rather than screenshots."],
      ["Track who you actually trade with", "Keep a short private list of reliable partners. Repeat trades with known people are where the community becomes genuinely useful."],
    ],
    data: [
      ["Best in-person lead", "Shops already running other Bushiroad card games."],
      ["Best online signal", "Pinned rules citing the publisher plus enforced trade formatting."],
      ["Trade requirement", "Exact printed English card code and condition wording."],
      ["Valuation source", "Live listing and sold data, never a single asking price."],
    ],
    pitfalls: [
      ["Joining ten groups at once", "Noise rises and signal does not. One moderated group plus a trade board covers everything."],
      ["Trading by Pal name", "Several printings share a name. Without the printed code you are agreeing to different cards."],
      ["Believing group price chatter", "Chat quotes asks, not sales. Confirm the number and the card's liquidity before you agree."],
      ["Showing up with your whole collection", "Bring a small labelled trade binder; a full collection invites pressure and loss."],
    ],
    faq: [
      ["Where can I find Palworld TCG players near me?", "Ask card shops that already run other Bushiroad titles which night they schedule Palworld play. They have the stock and the judges, so they usually run it first."],
      ["Do I need to join a Discord to play?", "No, but one moderated group makes pairing and trading much easier. Pick a group with pinned rules and enforced trade formatting rather than the largest one."],
      ["How do I vet an online Palworld TCG group?", "Look for pinned rules that cite the publisher, a trade channel requiring printed card codes and condition, and moderators who act on scam reports publicly."],
      ["Is it safe to trade with strangers online?", "It can be, if you quote exact printed codes, agree condition in writing, value both sides against live data, photograph the card before packing and use tracked shipping."],
      ["What is the fastest way into the community?", "Attend one casual shop night, trade one card properly, and post a short want list by card code. Those three steps produce more contacts than months of lurking."],
    ],
  },
  {
    slug: "palworld-tcg-playgroup",
    title: "Palworld TCG Playgroup Setup",
    desc: "How to start a Palworld TCG playgroup: finding a venue, keeping the first sessions welcoming, loaner decks, and turning a casual night into a regular one.",
    keywords:
      "start palworld tcg playgroup, palworld tcg casual night, palworld card game group, how to run palworld tcg meetup, palworld tcg beginners night",
    hub: "/deck-builder",
    hubLabel: "Palworld TCG deck builder",
    tag: "TCG Community",
    question: "How do you start a Palworld TCG playgroup?",
    answer:
      "A playgroup needs a fixed venue, a fixed time and two loaner decks. Fix the day before you recruit, because 'whenever suits' groups never form. Keep the first three sessions casual with no entry fee, lend decks to anyone curious, and only add structure such as Swiss rounds once the same four or five people keep returning.",
    steps: [
      ["Fix the venue and the day first", "A shop table, a library room or a café corner all work. Commit to a repeating day and time before you invite anybody, because scheduling by consensus stalls."],
      ["Build two loaner decks", "Two straightforward, legal decks of similar power let anyone play immediately. This single step converts more curious onlookers than any amount of promotion."],
      ["Keep the first sessions free and casual", "No entry fee, no locked lists, no prizes. The goal is a second session, not a winner."],
      ["Teach with the printed text visible", "Explain rules by reading card text rather than paraphrasing. New players learn faster and inherit fewer wrong habits."],
      ["Add structure only once attendance repeats", "When the same handful return, introduce short Swiss rounds and, later, a small prize pool. Structure before attendance kills groups."],
    ],
    data: [
      ["Minimum viable group", "Three regulars plus two loaner decks."],
      ["Session length", "Two to three hours covers several casual rounds."],
      ["Loaner deck design", "Similar power, simple energy curve, no fragile combos."],
      ["When to add rounds", "After the same four or five players return twice."],
    ],
    pitfalls: [
      ["Waiting for a perfect time slot", "Consensus scheduling never converges. Pick a day and let people opt in."],
      ["No loaner decks", "Curious people with no cards leave and do not come back."],
      ["Prizes too early", "Prizes attract competitive play before the group has a social core."],
      ["Teaching from memory", "Paraphrased rules spread errors that are painful to unlearn later."],
    ],
    faq: [
      ["How many people do you need for a playgroup?", "Three regulars is enough to sustain a night, and two loaner decks let newcomers join without owning cards."],
      ["Should a new playgroup use sanctioned rules?", "Not at first. Run casual sessions with open lists, then move toward sanctioned structure once attendance is stable."],
      ["What should loaner decks look like?", "Two legal decks of similar power with simple energy curves and no fragile combos, so a first-time player can win a game."],
      ["Where can a playgroup meet?", "A shop table is ideal because it brings passing traffic, but libraries, community rooms and cafés all work if the day is fixed."],
      ["How do you keep a playgroup alive?", "Same day, same place, loaner decks available, and no pressure to spend. Consistency retains players far better than prize support."],
    ],
  },
  {
    slug: "palworld-tcg-safe-trading",
    title: "Palworld TCG Safe Trading",
    desc: "A safety procedure for Palworld TCG trades: verifying printings, agreeing condition, photographing cards, shipping choices and what to do if a trade goes wrong.",
    keywords:
      "palworld tcg safe trading, palworld card trade scam, how to ship palworld tcg cards, palworld card condition grading trade, palworld tcg trade rules",
    hub: "/want-list",
    hubLabel: "Palworld TCG want list",
    tag: "TCG Community",
    question: "How do you trade Palworld TCG cards safely with strangers?",
    answer:
      "Safe trading is a procedure, not a judgement call. Confirm the exact printed card code on both sides, agree condition wording in writing, photograph each card next to something identifying before packing, ship tracked both ways, and value both sides against live market data. Every common scam depends on skipping one of those five steps, usually under time pressure.",
    steps: [
      ["Confirm both printings by code", "Ask for the printed English card code including any parallel suffix, and check it exists exactly as described before discussing value."],
      ["Agree condition in writing", "State centring, edges, surface and corners in words both sides accept. 'Near mint' alone is where disputes start."],
      ["Photograph before packing", "Photograph the front and back of each card next to a dated note or an identifying object. This is your only evidence if a card is swapped in transit."],
      ["Ship tracked, both directions", "Tracking is the difference between a resolvable dispute and a lost card. Insure anything meaningful."],
      ["Value both sides against live data", "Compare each card's live listing spread, sold data and liquidity. A thin card with one optimistic listing is not worth its ask."],
    ],
    data: [
      ["Non-negotiable", "Exact printed card code on both sides."],
      ["Condition language", "Centring, edges, surface, corners — stated explicitly."],
      ["Evidence", "Dated photos of front and back before packing."],
      ["Shipping", "Tracked both ways; insured above pocket-change value."],
      ["Graded cards", "Agree grade, grader and population before agreeing value."],
    ],
    pitfalls: [
      ["Accepting urgency", "Time pressure exists to stop you checking. A genuine trader will wait a day."],
      ["Stock photos instead of the real card", "Always require photos of the actual card, ideally with an identifying object in frame."],
      ["Untracked shipping to save postage", "The saving is trivial against the loss, and it removes any way to prove delivery."],
      ["Valuing graded cards like raw ones", "Grade and population change value dramatically; agree both before the trade."],
    ],
    faq: [
      ["What is the most common Palworld TCG trade scam?", "Sending a different printing with the same Pal name. Confirming the exact printed card code on both sides removes it entirely."],
      ["Should I always ship tracked?", "Yes. Untracked shipping leaves you no way to prove delivery, and the postage saving is trivial compared with the card value."],
      ["How do I describe condition fairly?", "State centring, edges, surface and corners explicitly, and send clear photos of front and back. Vague terms like 'near mint' cause most disputes."],
      ["How do I value a graded card in a trade?", "Agree the grader, grade and population first, then compare recent graded sales for that exact printing rather than raw prices."],
      ["What if a trade goes wrong?", "Use your dated pre-packing photos and tracking to open a dispute with the platform or payment provider, and report the trader to the group's moderators."],
    ],
  },
  {
    slug: "palworld-tcg-community-prices",
    title: "Palworld TCG Community Price Talk",
    desc: "How to read Palworld TCG community price claims: asks versus sales, hype cycles, low-volume distortion, and how to check any number in under a minute.",
    keywords:
      "palworld tcg card prices community, palworld card price hype, are palworld tcg cards worth it, palworld card market rumours, palworld tcg price check",
    hub: "/tcg-price-index",
    hubLabel: "Palworld TCG price index",
    tag: "TCG Community",
    question: "Can you trust community price claims for Palworld TCG cards?",
    answer:
      "Treat community price claims as leads, not facts. Most numbers repeated in groups are asking prices from a single listing, and low-volume cards let one optimistic seller define a 'market rate' that nobody has ever paid. Check the exact printing's live listing spread, its sold data and its liquidity before acting, and the number usually changes.",
    steps: [
      ["Identify the exact printing", "A price claim without a printed card code is unusable, because parallels and promos trade at very different levels."],
      ["Separate asks from sales", "Listings show what sellers want; sold data shows what buyers paid. Only the second one is a price."],
      ["Check liquidity before believing a move", "A card that trades rarely can swing wildly on one listing. Low liquidity means low confidence, regardless of the headline."],
      ["Compare against the index", "Set a card's move against the whole-market benchmark. Cards moving with the index are trends; cards moving alone are usually noise."],
      ["Decide before the hype, not during", "Write down the number you would pay and the number you would sell at, then act on your own thresholds instead of the thread's mood."],
    ],
    data: [
      ["Asking price", "What a seller wants. Not evidence of value."],
      ["Sold data", "What buyers actually paid. The only real price signal."],
      ["Liquidity score", "How often a printing trades. Low liquidity means low confidence."],
      ["Versus index", "Whether a move is market-wide or specific to one card."],
      ["Spread", "Distance between low ask and high ask; wide spreads mean uncertainty."],
    ],
    pitfalls: [
      ["Quoting one listing as the market", "One optimistic ask is a rumour. Look at the spread and the volume behind it."],
      ["Ignoring liquidity", "Thin cards produce the loudest and least reliable price talk in every community."],
      ["Buying during a hype spike", "Spikes driven by content rather than supply usually retrace. Set your price first."],
      ["Mixing raw and graded comps", "Graded sales are a different market. Compare like with like, including population."],
    ],
    faq: [
      ["Why do community prices differ from the index?", "Communities usually quote single asking prices, while an index aggregates many observations. The index is the better reference for whether a move is real."],
      ["What does liquidity tell me about a price?", "How much to trust it. A printing that trades often has a dependable price; a printing that rarely trades can be repriced by one listing."],
      ["Is a card 'about to explode' ever true?", "Occasionally, but the claim is unfalsifiable and usually arrives after the move. Compare the card against the index and its own sold history instead."],
      ["How do I price-check a card quickly?", "Look up the exact printed code, read the live low and high asks, the sold data and the liquidity score. That takes under a minute and beats any thread."],
      ["Should I sell into community hype?", "If the price exceeds the sell threshold you set in advance, yes. Deciding thresholds before the hype is what makes the decision reliable."],
    ],
  },
  {
    slug: "palworld-tcg-event-organizing",
    title: "Palworld TCG Event Organizing",
    desc: "How to organise a Palworld TCG event: pairings, round timing, prize structure, ruling disputes, and the logistics that keep a small tournament running smoothly.",
    keywords:
      "organise palworld tcg tournament, palworld tcg event pairings, palworld tcg round timing, run palworld card tournament, palworld tcg prize structure",
    hub: "/tcg-tools",
    hubLabel: "Palworld TCG tools",
    tag: "TCG Community",
    question: "How do you organise a small Palworld TCG tournament?",
    answer:
      "Small events succeed on timing and clarity. Publish the format, entry fee, start time and round length in advance, cap the event at the number of rounds the venue's hours allow, pair with Swiss, and announce a single ruling process before round one. Collect written deck lists if the event is sanctioned, and keep the prize structure flat enough that new players still enter.",
    steps: [
      ["Publish the details before the day", "Format, entry fee, start time, round length and prize structure. Ambiguity on any of these produces arguments at registration."],
      ["Size the event to the venue hours", "Work backwards: rounds times round length plus turnaround must fit the booking, with slack for a top cut if you run one."],
      ["Pair with Swiss and post standings", "Swiss keeps everyone playing all day. Post standings between rounds so nobody has to ask."],
      ["Announce the ruling process first", "State who rules, that printed card text decides, and that the organiser's call is final for the day. Do this before round one, not during a dispute."],
      ["Keep prizes flat", "Flat prize support keeps casual players entering. Top-heavy prizes at a small event thin the field fast."],
    ],
    data: [
      ["Pairing system", "Swiss — everyone plays every round."],
      ["Round length", "Fixed and announced; include turnaround in your schedule."],
      ["Deck lists", "Written lists required for sanctioned events."],
      ["Rulings", "Printed card text decides; organiser's call final for the day."],
      ["Prize shape", "Flat structures retain casual entrants."],
    ],
    pitfalls: [
      ["Too many rounds for the hours", "Events that overrun lose the venue and the players. Size rounds to the booking."],
      ["No published round length", "Without a clock, rounds drift and the final round starts too late to finish."],
      ["Improvising a ruling policy", "Deciding process mid-dispute makes any outcome feel arbitrary. Announce it up front."],
      ["Top-heavy prizes at a tiny event", "New players stop entering when only the top table is rewarded."],
    ],
    faq: [
      ["How many rounds should a small event run?", "As many as the venue hours allow after accounting for round length and turnaround. Finishing on time matters more than an extra round."],
      ["Do I need written deck lists?", "For sanctioned events, yes. For casual nights they are optional, though they speed up any dispute about legality."],
      ["How should I handle a rules dispute?", "Announce before round one that printed card text decides and the organiser's call is final for the day, then apply that consistently."],
      ["What prize structure works best for small events?", "Flat support spread across more players. Top-heavy prizes discourage the casual entrants a small scene depends on."],
      ["What do I need to run pairings?", "Swiss pairings and posted standings between rounds. Any pairing tool or a spreadsheet works at small attendance."],
    ],
  },
];

const POOL = OFFICIAL_LINKS;
function pool(offset: number) {
  return [POOL[offset % POOL.length], POOL[(offset + 1) % POOL.length], POOL[(offset + 2) % POOL.length]];
}

function articles(t: Topic): GuideArticle[] {
  const hubSentence = `Keep the ${t.hubLabel} open at ${t.hub} while you work through this, so every value and listing you act on is the live one.`;
  return [
    {
      title: t.title,
      slug: "overview",
      intro: `${t.answer} ${hubSentence}`,
      sections: [
        { heading: t.question, text: t.answer },
        { heading: "Why this matters", text: `${t.steps[0][1]} ${t.pitfalls[0][1]}` },
        { heading: "What the community actually rewards", text: t.data.map(([k, v]) => `${k}: ${v}`).join(" ") },
        { heading: `Use it with the ${t.hubLabel}`, text: `${hubSentence} Work from the tool's output rather than a number you remember from a thread.` },
      ],
      externalLinks: pool(5),
    },
    {
      title: `${t.title}: step-by-step`,
      slug: "step-by-step",
      intro: "The order to follow. Each step removes a failure that costs players cards, money or time.",
      sections: t.steps.map(([heading, text], i) => ({ heading: `${i + 1}. ${heading}`, text })),
      externalLinks: pool(2),
    },
    {
      title: `${t.title}: reference table`,
      slug: "reference",
      intro: `The definitions and thresholds behind ${t.title.toLowerCase()}, in the order you will need them.`,
      sections: t.data.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(0),
    },
    {
      title: `${t.title}: mistakes and fixes`,
      slug: "mistakes",
      intro: "Four failure modes that account for most bad community outcomes on this topic, each with the fix.",
      sections: t.pitfalls.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(3),
    },
    {
      title: `${t.title}: FAQ`,
      slug: "faq",
      intro: `Direct answers to the questions players ask about ${t.title.toLowerCase()}.`,
      sections: t.faq.map(([q, a]) => ({ heading: q, text: a })),
      externalLinks: pool(1),
    },
  ];
}

export const TCG_COMMUNITY_GUIDES: Guide[] = TOPICS.map((t) => ({
  slug: t.slug,
  title: `${t.title} Guide & FAQ`,
  desc: t.desc,
  tag: t.tag,
  updated: "2026-09-02",
  keywords: t.keywords,
  articles: articles(t),
}));

/** Hub page each community cluster supports, for internal-link rendering. */
export const TCG_COMMUNITY_CLUSTERS: Record<string, { slug: string; title: string }[]> = TOPICS.reduce(
  (acc, t) => {
    (acc[t.hub] ??= []).push({ slug: t.slug, title: t.title });
    return acc;
  },
  {} as Record<string, { slug: string; title: string }[]>,
);
